import { html } from '../lib/html.js';
import { busy, toastError, toastSuccess } from '../lib/ui.js';
import { MAX_PHOTOS, uploadClaimFiles, vehicles } from '../services/vehicles.js';

/** Resume a confirmation whose upload and Vio recognition already succeeded. */
export const documentConfirmation = (claim) => html`
  <div class="panel">
    <strong>Documento recebido</strong>
    <p>Confirme os dados lidos pela Vio para concluir o cadastro.</p>
    ${[['Placa', 'plate'], ['RENAVAM', 'renavam'], ['Marca', 'brand'], ['Modelo', 'model'], ['Ano', 'modelYear'], ['Cor', 'color'], ['Chassi', 'serialNumber'], ['Apelido', 'nickname']].map(([label, key]) => html`<p><b>${label}:</b> ${claim.submitted?.[key] || '—'}</p>`)}
    <ion-button expand="block" class="confirm-document">Confirmar cadastro</ion-button>
  </div>`;

export function bindDocumentConfirmation(root, claim, onDone) {
  const button = root.$('.confirm-document');
  button.addEventListener('click', () => busy(button, async () => {
    try {
      const r = await vehicles.submitClaim(claim.id);
      toastSuccess(r.claim.status === 'APPROVED' ? 'Documento aprovado automaticamente!' : 'Documento enviado para análise.');
      await onDone(r.claim);
    } catch (error) { toastError(error.message); }
  }));
}

/**
 * Caixa de envio do CRLV-e (ou da nota fiscal, para máquina agrícola), usada no cadastro e na tela do veículo.
 *   uploadBox({ category })             → html
 *   bindUpload(root, claimId, onDone)   → liga câmera/arquivo, miniaturas, progresso e conclusão
 *
 * Foto: a pessoa tira quantas precisar (frente e verso, até MAX_PHOTOS) e vê as miniaturas;
 * no envio, as fotos viram UMA imagem (uma embaixo da outra), que o admin vê de uma vez.
 * PDF: vai sozinho, como está.
 */
export const uploadBox = ({ category = 'LIGHT' } = {}) => html`
  <ion-card class="upload-box">
    <ion-card-content>
      <div class="upload-head">
        ${category === 'AGRO'
          ? html`<strong>Envie a nota fiscal ou o CRLV</strong>
            <p>Nota fiscal de compra da máquina (PDF ou foto nítida). Se ela for emplacada, pode ser o CRLV-e. O documento precisa estar no seu nome ou no da sua empresa.</p>`
          : html`<strong>Envie o CRLV-e</strong>
            <p>PDF do app Carteira Digital de Trânsito, ou fotos nítidas da frente e do verso do documento.</p>`}
      </div>
      <input type="file" class="pick-photo" accept="image/*" capture="environment" hidden />
      <input type="file" class="pick-file" accept="application/pdf,image/*" multiple hidden />
      <div class="thumbs" hidden></div>
      <p class="thumbs-hint hint small" hidden></p>
      <div class="upload-actions">
        <ion-button expand="block" class="btn-photo"><ion-icon slot="start" name="camera-outline"></ion-icon><span class="btn-photo-label">Tirar foto</span></ion-button>
        <ion-button expand="block" fill="outline" class="btn-file"><ion-icon slot="start" name="document-attach-outline"></ion-icon>Escolher arquivo</ion-button>
        <ion-button expand="block" color="success" class="btn-send" hidden><ion-icon slot="start" name="checkmark-circle"></ion-icon>Enviar documento</ion-button>
      </div>
      <div class="upload-progress" hidden>
        <p class="upload-label">Enviando...</p>
        <ion-progress-bar value="0"></ion-progress-bar>
      </div>
      <p class="hint small">O documento fica guardado com acesso restrito: só você e a equipe de análise do TicketCar podem vê-lo.</p>
    </ion-card-content>
  </ion-card>`;

/**
 * @param {HTMLElement} root
 * @param {string} claimId
 * @param {(claim: any) => void} onDone
 */
export function bindUpload(root, claimId, onDone) {
  const box = root.querySelector('.upload-box');
  const photo = box.querySelector('.pick-photo');
  const file = box.querySelector('.pick-file');
  const thumbs = box.querySelector('.thumbs');
  const hint = box.querySelector('.thumbs-hint');
  const actions = box.querySelector('.upload-actions');
  const btnPhoto = box.querySelector('.btn-photo');
  const btnSend = box.querySelector('.btn-send');
  const progress = box.querySelector('.upload-progress');
  const bar = box.querySelector('ion-progress-bar');
  const label = box.querySelector('.upload-label');

  /** @type {{ file: File, url?: string }[]} */
  let items = [];
  const isPdf = () => items.length === 1 && items[0].file.type === 'application/pdf';

  const render = () => {
    thumbs.hidden = !items.length;
    thumbs.innerHTML = items
      .map((it, i) =>
        it.file.type === 'application/pdf'
          ? html`<div class="thumb pdf"><ion-icon name="document-text-outline"></ion-icon><span>${it.file.name}</span><button type="button" class="thumb-x" data-i="${i}" aria-label="Remover">×</button></div>`.value
          : html`<div class="thumb"><img src="${it.url}" alt="Foto ${i + 1}" /><span class="thumb-n">${i + 1}</span><button type="button" class="thumb-x" data-i="${i}" aria-label="Remover foto ${i + 1}">×</button></div>`.value,
      )
      .join('');
    hint.hidden = !items.length;
    hint.textContent = isPdf()
      ? 'PDF selecionado.'
      : items.length === 1
        ? '1 foto. Se o documento tiver verso, tire mais uma.'
        : `${items.length} fotos. Elas vão juntas, em uma imagem só.`;
    btnSend.hidden = !items.length;
    btnPhoto.hidden = isPdf() || items.length >= MAX_PHOTOS;
    box.querySelector('.btn-photo-label').textContent = items.length ? 'Tirar outra foto' : 'Tirar foto';
  };

  const add = (files) => {
    for (const f of files) {
      if (f.type === 'application/pdf') {
        clear();
        items = [{ file: f }]; // PDF vai sozinho
        break;
      }
      if (!f.type.startsWith('image/')) continue;
      if (isPdf()) clear();
      if (items.length >= MAX_PHOTOS) {
        toastError(`No máximo ${MAX_PHOTOS} fotos.`);
        break;
      }
      items.push({ file: f, url: URL.createObjectURL(f) });
    }
    render();
  };

  const clear = () => {
    items.forEach((it) => it.url && URL.revokeObjectURL(it.url));
    items = [];
  };

  thumbs.addEventListener('click', (e) => {
    const x = e.target.closest('.thumb-x');
    if (!x) return;
    const [it] = items.splice(Number(x.dataset.i), 1);
    if (it?.url) URL.revokeObjectURL(it.url);
    render();
  });
  btnPhoto.addEventListener('click', () => photo.click());
  box.querySelector('.btn-file').addEventListener('click', () => file.click());
  for (const input of [photo, file]) {
    input.addEventListener('change', () => {
      add([...(input.files ?? [])]);
      input.value = '';
    });
  }

  btnSend.addEventListener('click', async () => {
    if (!items.length) return;
    actions.hidden = true;
    thumbs.classList.add('locked');
    progress.hidden = false;
    bar.value = 0;
    label.textContent = items.length > 1 ? 'Juntando as fotos...' : 'Preparando o arquivo...';
    try {
      const r = await uploadClaimFiles(claimId, items.map((it) => it.file), (x) => {
        bar.value = x;
        label.textContent = `Enviando... ${Math.round(x * 100)}%`;
      });
      label.textContent = 'Enviado!';
      clear();
      onDone(r.claim);
    } catch (err) {
      toastError(err.message);
      actions.hidden = false;
      thumbs.classList.remove('locked');
      progress.hidden = true;
    }
  });
}
